import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  Globe,
  Plus,
  Edit2,
  Trash2,
  AlertCircle,
  X,
  Search,
  RefreshCw,
  Filter,
  Activity,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from 'lucide-react';
import toast from 'react-hot-toast';
import '../styles/responsive/pages/Networks.css';
import { useTheme } from '../context/ThemeContext';
import { fetchWithAuth } from '../utils/api';
import useDebounce from '../hooks/useDebounce';
import { ChainLogo } from '../components/common/CryptoLogo';
import Skeleton from '../components/common/Skeleton';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const NetworkModal = ({ isOpen, onClose, network, onSave, isLoading }) => {
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    networkType: 'EVM',
    chainId: '',
    rpcUrl: '',
    rpcUrlBackup: '',
    explorerUrl: '',
    explorerApiUrl: '',
    nativeCurrency: '',
    nativeCurrencyDecimals: 18,
    logoUrl: '',
    isTestnet: false,
    status: 'active',
    displayOrder: 0,
    blockConfirmations: 1,
    alchemyWebhookId: '',
    alchemySigningKey: '',
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (network) {
      setFormData({
        name: network.name || '',
        slug: network.slug || '',
        networkType: network.networkType || 'EVM',
        chainId: network.chainId || '',
        rpcUrl: network.rpcUrl || '',
        rpcUrlBackup: network.rpcUrlBackup || '',
        explorerUrl: network.explorerUrl || '',
        explorerApiUrl: network.explorerApiUrl || '',
        nativeCurrency: network.nativeCurrency || '',
        nativeCurrencyDecimals: network.nativeCurrencyDecimals || 18,
        logoUrl: network.logoUrl || '',
        isTestnet: network.isTestnet || false,
        status: network.status || 'active',
        displayOrder: network.displayOrder || 0,
        blockConfirmations: network.blockConfirmations || 1,
        alchemyWebhookId: network.alchemyWebhookId || '',
        alchemySigningKey: network.alchemySigningKey || '',
      });
    } else {
      setFormData({
        name: '',
        slug: '',
        networkType: 'EVM',
        chainId: '',
        rpcUrl: '',
        rpcUrlBackup: '',
        explorerUrl: '',
        explorerApiUrl: '',
        nativeCurrency: '',
        nativeCurrencyDecimals: 18,
        logoUrl: '',
        isTestnet: false,
        status: 'active',
        displayOrder: 0,
        blockConfirmations: 1,
        alchemyWebhookId: '',
        alchemySigningKey: '',
      });
    }
    setErrors({});
  }, [network, isOpen]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Network name is required';
    if (!formData.slug.trim()) newErrors.slug = 'Slug is required';
    if (formData.slug && !/^[a-z0-9-]+$/.test(formData.slug)) {
      newErrors.slug = 'Slug can only contain lowercase letters, numbers, and hyphens';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      onSave(formData);
    }
  };

  const generateSlug = () => {
    const slug = formData.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    setFormData({ ...formData, slug });
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-container modal-large">
        <div className="modal-header">
          <h3>{network ? 'Edit Network' : 'Add New Network'}</h3>
          <button className="modal-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-row">
              <div className="form-group">
                <label>Network Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Ethereum Mainnet"
                  className={errors.name ? 'input-error' : ''}
                />
                {errors.name && <span className="error-text"><AlertCircle size={14} /> {errors.name}</span>}
              </div>
              <div className="form-group">
                <label>
                  Slug *
                  <button type="button" className="btn-link" onClick={generateSlug} style={{ marginLeft: '8px', fontSize: '12px' }}>
                    Generate from name
                  </button>
                </label>
                <input
                  type="text"
                  value={formData.slug}
                  onChange={(e) => setFormData({ ...formData, slug: e.target.value.toLowerCase() })}
                  placeholder="e.g., ethereum"
                  className={errors.slug ? 'input-error' : ''}
                />
                {errors.slug && <span className="error-text"><AlertCircle size={14} /> {errors.slug}</span>}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Network Type</label>
                <select
                  value={formData.networkType}
                  onChange={(e) => setFormData({ ...formData, networkType: e.target.value })}
                >
                  <option value="EVM">EVM (Ethereum Virtual Machine)</option>
                  <option value="Solana">Solana</option>
                  <option value="Bitcoin">Bitcoin</option>
                  <option value="Tron">Tron</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div className="form-group">
                <label>Chain ID {formData.networkType === 'EVM' && '(Required for EVM)'}</label>
                <input
                  type="number"
                  value={formData.chainId}
                  onChange={(e) => setFormData({ ...formData, chainId: e.target.value })}
                  placeholder="e.g., 1"
                />
              </div>
            </div>

            <div className="form-group">
              <label>Primary RPC URL</label>
              <input
                type="url"
                value={formData.rpcUrl}
                onChange={(e) => setFormData({ ...formData, rpcUrl: e.target.value })}
                placeholder="https://mainnet.infura.io/v3/YOUR_KEY"
              />
            </div>

            <div className="form-group">
              <label>Backup RPC URL</label>
              <input
                type="url"
                value={formData.rpcUrlBackup}
                onChange={(e) => setFormData({ ...formData, rpcUrlBackup: e.target.value })}
                placeholder="https://eth-mainnet.alchemyapi.io/v2/YOUR_KEY"
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Block Explorer URL</label>
                <input
                  type="url"
                  value={formData.explorerUrl}
                  onChange={(e) => setFormData({ ...formData, explorerUrl: e.target.value })}
                  placeholder="https://etherscan.io"
                />
              </div>
              <div className="form-group">
                <label>Explorer API URL</label>
                <input
                  type="url"
                  value={formData.explorerApiUrl}
                  onChange={(e) => setFormData({ ...formData, explorerApiUrl: e.target.value })}
                  placeholder="https://api.etherscan.io/api"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Native Currency Symbol</label>
                <input
                  type="text"
                  value={formData.nativeCurrency}
                  onChange={(e) => setFormData({ ...formData, nativeCurrency: e.target.value.toUpperCase() })}
                  placeholder="e.g., ETH"
                  maxLength={10}
                />
              </div>
              <div className="form-group">
                <label>Native Currency Decimals</label>
                <input
                  type="number"
                  value={formData.nativeCurrencyDecimals}
                  onChange={(e) => setFormData({ ...formData, nativeCurrencyDecimals: parseInt(e.target.value) || 18 })}
                  min={0}
                  max={18}
                />
              </div>
            </div>

            <div className="form-group">
              <label>Logo URL</label>
              <input
                type="url"
                value={formData.logoUrl}
                onChange={(e) => setFormData({ ...formData, logoUrl: e.target.value })}
                placeholder="https://example.com/logo.png"
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Display Order</label>
                <input
                  type="number"
                  value={formData.displayOrder}
                  onChange={(e) => setFormData({ ...formData, displayOrder: parseInt(e.target.value) || 0 })}
                  min={0}
                />
              </div>
              <div className="form-group">
                <label>Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <label>Block Confirmations</label>
              <input
                type="number"
                value={formData.blockConfirmations}
                onChange={(e) => setFormData({ ...formData, blockConfirmations: parseInt(e.target.value) || 1 })}
                min={1}
                placeholder="Number of confirmations required"
              />
              <span className="form-hint">Number of block confirmations required for transactions on this network</span>
            </div>

            <div className="form-section-divider">
              <span>Alchemy Webhook Configuration</span>
            </div>

            <div className="form-group">
              <label>Alchemy Webhook ID</label>
              <input
                type="text"
                value={formData.alchemyWebhookId}
                onChange={(e) => setFormData({ ...formData, alchemyWebhookId: e.target.value })}
                placeholder="e.g., wh_xxxxxxxxxxxx"
              />
            </div>

            <div className="form-group">
              <label>Alchemy Signing Key</label>
              <input
                type="password"
                value={formData.alchemySigningKey}
                onChange={(e) => setFormData({ ...formData, alchemySigningKey: e.target.value })}
                placeholder="e.g., whsec_xxxxxxxxxxxx"
              />
            </div>

            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.isTestnet}
                  onChange={(e) => setFormData({ ...formData, isTestnet: e.target.checked })}
                />
                <span>This is a testnet</span>
              </label>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn-secondary" onClick={onClose} disabled={isLoading}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={isLoading}>
              {isLoading ? 'Saving...' : (network ? 'Update Network' : 'Add Network')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const ConfirmModal = ({ isOpen, onClose, onConfirm, title, message, isLoading, confirmText = 'Confirm' }) => {
  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-container modal-small">
        <div className="modal-header">
          <h3>{title}</h3>
          <button className="modal-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>
        <div className="modal-body">
          <p>{message}</p>
        </div>
        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose} disabled={isLoading}>
            Cancel
          </button>
          <button className="btn-danger" onClick={onConfirm} disabled={isLoading}>
            {isLoading ? 'Processing...' : confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

const FilterModal = ({ isOpen, onClose, filters, onApply, isSuperAdmin }) => {
  const [localFilters, setLocalFilters] = useState(filters);

  useEffect(() => {
    setLocalFilters(filters);
  }, [filters, isOpen]);

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-container modal-small">
        <div className="modal-header">
          <h3>Filter Networks</h3>
          <button className="modal-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>
        <div className="modal-body">
          {isSuperAdmin && (
            <div className="form-group">
              <label>Status</label>
              <select
                value={localFilters.status}
                onChange={(e) => setLocalFilters({ ...localFilters, status: e.target.value })}
              >
                <option value="all">All</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          )}

          <div className="form-group">
            <label>Network Type</label>
            <select
              value={localFilters.networkType}
              onChange={(e) => setLocalFilters({ ...localFilters, networkType: e.target.value })}
            >
              <option value="all">All</option>
              <option value="EVM">EVM</option>
              <option value="Non-EVM">Non-EVM</option>
            </select>
          </div>

          <div className="form-group">
            <label>Sort By</label>
            <select
              value={localFilters.sortBy}
              onChange={(e) => setLocalFilters({ ...localFilters, sortBy: e.target.value })}
            >
              <option value="display_order">Display Order</option>
              <option value="name">Name</option>
              <option value="created_at">Created Date</option>
            </select>
          </div>
        </div>
        <div className="modal-footer">
          <button
            className="btn-secondary"
            onClick={() => {
              setLocalFilters({ status: 'all', networkType: 'all', sortBy: 'display_order' });
            }}
          >
            Reset
          </button>
          <button
            className="btn-primary"
            onClick={() => {
              onApply(localFilters);
              onClose();
            }}
          >
            Apply Filters
          </button>
        </div>
      </div>
    </div>
  );
};

const Networks = () => {
  const { token: authToken, user } = useSelector((state) => state.auth);
  const { mode } = useTheme();
  const isSuperAdmin = user?.role_id === 1 || user?.roleId === 1;
  const isTestnetMode = mode === 'test';

  const [networks, setNetworks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 400);
  const [filters, setFilters] = useState({
    status: 'all',
    networkType: 'all',
    sortBy: 'display_order',
  });
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [networkModalOpen, setNetworkModalOpen] = useState(false);
  const [editingNetwork, setEditingNetwork] = useState(null);
  const [confirmModal, setConfirmModal] = useState({ open: false, title: '', message: '', onConfirm: null, confirmText: 'Confirm' });
  const [isSaving, setIsSaving] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, totalPages: 0 });
  // NET-01: live RPC health, keyed by network id (operator only).
  const [health, setHealth] = useState({});
  const [healthLoading, setHealthLoading] = useState(false);

  const fetchNetworks = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit,
        search: debouncedSearch,
        sortBy: filters.sortBy,
        sortOrder: 'ASC',
      });

      if (filters.status !== 'all') params.append('status', filters.status);
      // EVM filters by exact server-side match. Non-EVM is rendered as Coming Soon
      // placeholders below, so it does not query the server by type.
      if (filters.networkType === 'EVM') params.append('networkType', 'EVM');
      params.append('isTestnet', isTestnetMode ? 'true' : 'false');

      const endpoint = isSuperAdmin ? '/networks' : '/networks/merchant';
      const response = await fetchWithAuth(`${API_BASE_URL}${endpoint}?${params}`);
      const data = await response.json();

      if (data.success) {
        setNetworks(data.data.networks);
        setPagination((prev) => ({
          ...prev,
          total: data.data.pagination?.total || data.data.networks.length,
          totalPages: data.data.pagination?.totalPages || 1,
        }));
      } else {
        toast.error(data.message || 'Failed to load networks');
      }
    } catch (error) {
      toast.error('Failed to load networks');
    } finally {
      setLoading(false);
    }
  }, [authToken, pagination.page, pagination.limit, debouncedSearch, filters, isSuperAdmin, isTestnetMode]);

  // Search/filter/mode changes return to page 1 (no-op re-render when already there).
  useEffect(() => {
    setPagination((prev) => (prev.page === 1 ? prev : { ...prev, page: 1 }));
  }, [debouncedSearch, filters, isTestnetMode]);

  useEffect(() => {
    fetchNetworks();
  }, [fetchNetworks]);

  // NET-01: fetch live RPC health and key it by network id. Operator-only endpoint.
  const fetchHealth = useCallback(async () => {
    if (!isSuperAdmin) return;
    setHealthLoading(true);
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/networks/health`);
      const data = await response.json();
      if (data.success) {
        const byId = {};
        (data.data.networks || []).forEach((h) => { byId[h.id] = h; });
        setHealth(byId);
      }
    } catch (error) {
      // Health is supplementary; a failure should not disrupt the page.
    } finally {
      setHealthLoading(false);
    }
  }, [isSuperAdmin]);

  useEffect(() => {
    fetchHealth();
  }, [fetchHealth]);

  const handleSaveNetwork = async (formData) => {
    setIsSaving(true);
    try {
      const url = editingNetwork
        ? `${API_BASE_URL}/networks/${editingNetwork.id}`
        : `${API_BASE_URL}/networks`;

      const response = await fetchWithAuth(url, {
        method: editingNetwork ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();
      if (data.success) {
        toast.success(data.message);
        setNetworkModalOpen(false);
        setEditingNetwork(null);
        fetchNetworks();
      } else {
        toast.error(data.message || 'Failed to save network');
      }
    } catch (error) {
      toast.error('Failed to save network');
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleGlobalStatus = async (network) => {
    if (network.status === 'active') {
      setConfirmModal({
        open: true,
        title: 'Disable Network',
        message: `Are you sure you want to disable "${network.name}"? This will also disable ALL tokens under this network.`,
        confirmText: 'Disable Network',
        onConfirm: async () => {
          await performToggleStatus(network.id, false);
          setConfirmModal({ open: false, title: '', message: '', onConfirm: null });
        },
      });
    } else {
      await performToggleStatus(network.id, true);
    }
  };

  const performToggleStatus = async (networkId, enabling) => {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/networks/${networkId}/toggle-status`, {
        method: 'PATCH',
      });
      const data = await response.json();
      if (data.success) {
        toast.success(data.message);
        fetchNetworks();
      } else {
        toast.error(data.message || 'Failed to toggle status');
      }
    } catch (error) {
      toast.error('Failed to toggle status');
    }
  };

  const handleToggleMerchantStatus = async (network) => {
    if (network.merchantEnabled) {
      setConfirmModal({
        open: true,
        title: 'Disable Network',
        message: `Are you sure you want to disable "${network.name}" for your payment flows? This will also disable all tokens under this network for your account.`,
        confirmText: 'Disable',
        onConfirm: async () => {
          await performMerchantToggle(network.id);
          setConfirmModal({ open: false, title: '', message: '', onConfirm: null });
        },
      });
    } else {
      await performMerchantToggle(network.id);
    }
  };

  const performMerchantToggle = async (networkId) => {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/networks/${networkId}/merchant-toggle`, {
        method: 'PATCH',
      });
      const data = await response.json();
      if (data.success) {
        toast.success(data.message);
        fetchNetworks();
      } else {
        toast.error(data.message || 'Failed to toggle network preference');
      }
    } catch (error) {
      toast.error('Failed to toggle network preference');
    }
  };

  const handleDeleteNetwork = async (network) => {
    setConfirmModal({
      open: true,
      title: 'Delete Network',
      message: `Are you sure you want to delete "${network.name}"? This action cannot be undone.`,
      confirmText: 'Delete',
      onConfirm: async () => {
        setIsSaving(true);
        try {
          const response = await fetchWithAuth(`${API_BASE_URL}/networks/${network.id}`, {
            method: 'DELETE',
          });
          const data = await response.json();
          if (data.success) {
            toast.success(data.message);
            fetchNetworks();
          } else {
            toast.error(data.message || 'Failed to delete network');
          }
        } catch (error) {
          toast.error('Failed to delete network');
        } finally {
          setIsSaving(false);
          setConfirmModal({ open: false, title: '', message: '', onConfirm: null });
        }
      },
    });
  };

  const filteredNetworks = networks.filter(
    (network) =>
      network.name.toLowerCase().includes(search.toLowerCase()) ||
      network.slug?.toLowerCase().includes(search.toLowerCase()) ||
      network.nativeCurrency?.toLowerCase().includes(search.toLowerCase())
  );

  const activeFilterCount = Object.values(filters).filter((v) => v !== 'all' && v !== 'display_order').length;

  // Non-EVM chains are not yet supported; shown blurred with a Coming Soon badge.
  const comingSoonNetworks = [
    { slug: 'bitcoin', name: 'Bitcoin', nativeCurrency: 'BTC' },
    { slug: 'solana', name: 'Solana', nativeCurrency: 'SOL' },
  ];

  // Map a network slug/name to its CHAIN color token so each card carries the
  // chain's brand color. Falls back to the strong line color for chains without
  // a dedicated token.
  const chainColor = (chain) => {
    const key = String(chain || '').toLowerCase();
    if (key.includes('eth')) return 'var(--chain-eth)';
    if (key.includes('bnb') || key.includes('binance') || key.includes('bsc')) return 'var(--chain-bnb)';
    if (key.includes('polygon') || key.includes('matic')) return 'var(--chain-polygon)';
    if (key.includes('tron')) return 'var(--chain-tron)';
    if (key.includes('solana') || key.includes('sol')) return 'var(--chain-solana)';
    return 'var(--line-strong)';
  };

  return (
    <div className="networks-page p-0">
      <div className="rd-page-header">
        <div>
          <h1 className="rd-page-title">Networks &amp; tokens</h1>
          <p className="rd-page-subtitle">
            {isSuperAdmin
              ? 'Manage blockchain networks. Disabling a network will cascade to all its tokens.'
              : 'Choose which chains and tokens your customers can pay with.'}
          </p>
        </div>
        <div className="nt-header-actions">
          {isSuperAdmin && (
            <button className="rd-btn rd-btn-secondary" onClick={fetchHealth} disabled={healthLoading}>
              <Activity size={16} />
              Health check
            </button>
          )}
          {isSuperAdmin && (
            <button
              className="rd-btn rd-btn-primary"
              onClick={() => {
                setEditingNetwork(null);
                setNetworkModalOpen(true);
              }}
            >
              <Plus size={16} />
              Add network
            </button>
          )}
        </div>
      </div>

      <div className="rd-tabs nt-tabs">
        <Link to="/dashboard/networks" className="rd-tab active">
          Networks
          {!loading && <span className="rd-tab-count">{pagination.total || filteredNetworks.length}</span>}
        </Link>
        <Link to="/dashboard/tokens" className="rd-tab">
          Tokens
        </Link>
      </div>

      <div className="search-filter-bar">
        <div className="search-box">
          <Search size={18} />
          <input
            type="text"
            placeholder="Search networks..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <button
          className={`btn-filter ${activeFilterCount > 0 ? 'active' : ''}`}
          onClick={() => setFilterModalOpen(true)}
        >
          <Filter size={18} />
          Filters
          {activeFilterCount > 0 && <span className="filter-count">{activeFilterCount}</span>}
        </button>
        <button className="btn-icon-only" onClick={fetchNetworks} title="Refresh">
          <RefreshCw size={18} className={loading ? 'spinning' : ''} />
        </button>
      </div>

      <div className="networks-grid">
        {filters.networkType === 'Non-EVM' ? (
          comingSoonNetworks.map((cs) => (
            <div key={cs.slug} className="network-card-wrap">
              <div
                className="rd-card network-card coming-soon"
                style={{ '--chain-c': chainColor(cs.slug) }}
                aria-hidden="true"
              >
                <div className="network-card-top">
                  <ChainLogo chain={cs.slug} size={40} />
                  <div className="network-title">
                    <h3>{cs.name}</h3>
                    <span className="network-sub">{cs.nativeCurrency}</span>
                  </div>
                  <label className="rd-switch">
                    <input type="checkbox" checked={false} disabled readOnly />
                    <span className="slider" />
                  </label>
                </div>
                <div className="network-card-stats">
                  <div className="nt-stat">
                    <span className="nt-stat-label">Currency</span>
                    <span className="nt-stat-value rd-mono">{cs.nativeCurrency}</span>
                  </div>
                  <div className="nt-stat">
                    <span className="nt-stat-label">Status</span>
                    <span className="rd-badge warn">Coming soon</span>
                  </div>
                </div>
              </div>
              <span className="coming-soon-badge">Coming Soon</span>
            </div>
          ))
        ) : loading ? (
          Array.from({ length: 6 }, (_, i) => (
            <Skeleton key={i} variant="card" />
          ))
        ) : filteredNetworks.length === 0 ? (
          <div className="rd-empty nt-state">
            <div className="rd-empty-icon"><Globe size={20} /></div>
            <h3>No networks found</h3>
            <p>
              {isSuperAdmin
                ? 'Add a blockchain network to start accepting payments.'
                : 'No networks are available for your account yet.'}
            </p>
            {isSuperAdmin && (
              <button
                className="rd-btn rd-btn-primary nt-empty-btn"
                onClick={() => {
                  setEditingNetwork(null);
                  setNetworkModalOpen(true);
                }}
              >
                <Plus size={16} />
                Add first network
              </button>
            )}
          </div>
        ) : (
          filteredNetworks.map((network) => {
            const h = health[network.id];
            const merchantOn = network.merchantEnabled !== false;
            const adminOn = network.status === 'active';
            return (
              <div
                key={network.id}
                className={`rd-card rd-card-hover network-card ${network.status === 'inactive' ? 'inactive' : ''} ${
                  !isSuperAdmin && network.merchantEnabled === false ? 'merchant-disabled' : ''
                }`}
                style={{ '--chain-c': chainColor(network.slug || network.name) }}
              >
                <div className="network-card-top">
                  <ChainLogo chain={network.slug || network.name} logoUrl={network.logoUrl} size={40} />
                  <div className="network-title">
                    <h3>{network.name}</h3>
                    <span className="network-sub">
                      {[network.nativeCurrency, `${network.blockConfirmations || 1} confirms`]
                        .filter(Boolean)
                        .join(' · ')}
                    </span>
                  </div>
                  {isSuperAdmin ? (
                    <label className="rd-switch" title={adminOn ? 'Disable (cascades to tokens)' : 'Enable'}>
                      <input
                        type="checkbox"
                        checked={adminOn}
                        onChange={() => handleToggleGlobalStatus(network)}
                      />
                      <span className="slider" />
                    </label>
                  ) : (
                    <label className="rd-switch" title={merchantOn ? 'Disable' : 'Enable'}>
                      <input
                        type="checkbox"
                        checked={merchantOn}
                        onChange={() => handleToggleMerchantStatus(network)}
                      />
                      <span className="slider" />
                    </label>
                  )}
                </div>

                <div className="network-card-stats">
                  <div className="nt-stat">
                    <span className="nt-stat-label">Currency</span>
                    <span className="nt-stat-value rd-mono">{network.nativeCurrency || '—'}</span>
                  </div>
                  <div className="nt-stat">
                    <span className="nt-stat-label">Status</span>
                    {isSuperAdmin && h ? (
                      <span
                        className={`rd-badge ${h.rpcStatus === 'up' ? 'ok' : h.rpcStatus === 'unconfigured' ? 'warn' : 'err'}`}
                      >
                        {h.rpcStatus === 'up' ? 'RPC healthy' : h.rpcStatus === 'unconfigured' ? 'RPC not set' : 'RPC down'}
                      </span>
                    ) : adminOn || (!isSuperAdmin && merchantOn) ? (
                      <span className="rd-badge ok">{isSuperAdmin ? 'Active' : 'Enabled'}</span>
                    ) : (
                      <span className="rd-badge err">Disabled</span>
                    )}
                  </div>
                </div>

                {isSuperAdmin && h && h.rpcStatus === 'up' && (h.latencyMs != null || h.latestBlock != null) && (
                  <div className="network-rpc-detail rd-mono">
                    {h.latencyMs != null ? `${h.latencyMs} ms` : ''}
                    {h.latencyMs != null && h.latestBlock != null ? ' · ' : ''}
                    {h.latestBlock != null ? `block ${h.latestBlock.toLocaleString('en-US')}` : ''}
                  </div>
                )}

                {isSuperAdmin && (
                  <div className="network-card-admin">
                    <button
                      className="btn-icon"
                      onClick={() => {
                        setEditingNetwork(network);
                        setNetworkModalOpen(true);
                      }}
                      title="Edit"
                    >
                      <Edit2 size={15} />
                    </button>
                    <button
                      className="btn-icon btn-danger-icon"
                      onClick={() => handleDeleteNetwork(network)}
                      title="Delete"
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {!loading && filters.networkType !== 'Non-EVM' && pagination.totalPages > 1 && (
        <div className="pagination-bar">
          <span className="pagination-info">{pagination.total.toLocaleString('en-US')} networks</span>
          <div className="pagination-controls">
            <button
              className="page-btn"
              onClick={() => setPagination((p) => ({ ...p, page: 1 }))}
              disabled={pagination.page <= 1}
              aria-label="First page"
            >
              <ChevronsLeft size={16} />
            </button>
            <button
              className="page-btn"
              onClick={() => setPagination((p) => ({ ...p, page: Math.max(1, p.page - 1) }))}
              disabled={pagination.page <= 1}
              aria-label="Previous page"
            >
              <ChevronLeft size={16} />
            </button>
            <span className="page-indicator">Page {pagination.page} of {pagination.totalPages}</span>
            <button
              className="page-btn"
              onClick={() => setPagination((p) => ({ ...p, page: Math.min(p.totalPages, p.page + 1) }))}
              disabled={pagination.page >= pagination.totalPages}
              aria-label="Next page"
            >
              <ChevronRight size={16} />
            </button>
            <button
              className="page-btn"
              onClick={() => setPagination((p) => ({ ...p, page: p.totalPages }))}
              disabled={pagination.page >= pagination.totalPages}
              aria-label="Last page"
            >
              <ChevronsRight size={16} />
            </button>
          </div>
        </div>
      )}

      {!loading && !isSuperAdmin && filteredNetworks.length > 0 && (
        <div className="rd-card nt-upsell">
          <div className="nt-upsell-icon">
            <Activity size={20} />
          </div>
          <div className="nt-upsell-copy">
            <h3>Unlock more chains with Premium</h3>
            <p>Get additional networks plus more tokens including memecoins and emerging stablecoins.</p>
          </div>
          <Link to="/dashboard/billing" className="rd-btn rd-btn-purple nt-upsell-btn">
            Upgrade to Premium
          </Link>
        </div>
      )}

      <NetworkModal
        isOpen={networkModalOpen}
        onClose={() => {
          setNetworkModalOpen(false);
          setEditingNetwork(null);
        }}
        network={editingNetwork}
        onSave={handleSaveNetwork}
        isLoading={isSaving}
      />

      <FilterModal
        isOpen={filterModalOpen}
        onClose={() => setFilterModalOpen(false)}
        filters={filters}
        onApply={setFilters}
        isSuperAdmin={isSuperAdmin}
      />

      <ConfirmModal
        isOpen={confirmModal.open}
        onClose={() => setConfirmModal({ open: false, title: '', message: '', onConfirm: null })}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmText={confirmModal.confirmText}
        isLoading={isSaving}
      />

      <style>{`
        .networks-page { color: var(--ink); }

        .nt-header-actions {
          display: flex;
          align-items: center;
          gap: 10px;
          flex-wrap: wrap;
        }

        .nt-tabs { margin-bottom: 20px; }
        .networks-page .rd-tab { text-decoration: none; }

        .search-filter-bar {
          display: flex;
          gap: 10px;
          margin-bottom: 20px;
        }

        .search-box {
          flex: 1;
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 9px 14px;
          background: var(--bg-surface);
          border: 1px solid var(--line-strong);
          border-radius: var(--r-sm);
          box-shadow: var(--sh-sm);
        }
        .search-box input {
          flex: 1;
          border: none;
          background: transparent;
          outline: none;
          font-size: 14px;
          color: var(--ink);
        }
        .search-box input::placeholder { color: var(--ink-muted); }
        .search-box svg { color: var(--ink-muted); }

        .btn-filter {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 9px 14px;
          background: var(--bg-surface);
          border: 1px solid var(--line-strong);
          border-radius: var(--r-sm);
          box-shadow: var(--sh-sm);
          cursor: pointer;
          font-size: 14px;
          font-weight: 500;
          color: var(--ink);
          font-family: var(--iv-sans);
        }
        .btn-filter:hover { background: var(--bg-inset); }
        .btn-filter.active { border-color: var(--accent); color: var(--accent-ink); }

        .filter-count {
          background: var(--accent);
          color: #fff;
          font-size: 11px;
          padding: 1px 6px;
          border-radius: var(--r-pill);
          min-width: 18px;
          text-align: center;
        }

        .btn-icon-only {
          padding: 9px;
          background: var(--bg-surface);
          border: 1px solid var(--line-strong);
          border-radius: var(--r-sm);
          box-shadow: var(--sh-sm);
          cursor: pointer;
          color: var(--ink-secondary);
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }
        .btn-icon-only:hover { background: var(--bg-inset); color: var(--ink); }

        .networks-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
          gap: 16px;
        }

        /* Card carries the chain's brand color as a left edge accent. */
        .network-card {
          padding: 18px;
          border-left: 3px solid var(--chain-c, var(--line));
        }
        .network-card.inactive { opacity: 0.6; }

        /* Non-EVM Coming Soon: blur + disable the card, overlay a crisp badge. */
        .network-card-wrap { position: relative; }
        .network-card.coming-soon { filter: blur(2px); pointer-events: none; user-select: none; }
        .coming-soon-badge {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          background: var(--accent);
          color: #fff;
          font-size: 12px;
          font-weight: 700;
          letter-spacing: 0.03em;
          text-transform: uppercase;
          padding: 7px 14px;
          border-radius: var(--r-pill);
          box-shadow: var(--sh-pop);
          white-space: nowrap;
        }

        .network-card-top {
          display: flex;
          align-items: center;
          gap: 12px;
        }
        .network-title { flex: 1; min-width: 0; }
        .network-title h3 {
          margin: 0;
          font-size: 15px;
          font-weight: 700;
          letter-spacing: -0.01em;
          color: var(--ink);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .network-sub {
          font-size: 12.5px;
          color: var(--t-500);
          font-family: var(--font-mono);
        }

        .network-card-stats {
          display: flex;
          align-items: flex-end;
          justify-content: space-between;
          gap: 16px;
          margin-top: 16px;
        }
        .nt-stat { display: flex; flex-direction: column; gap: 5px; min-width: 0; }
        .nt-stat-label {
          font-size: 11px;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          color: var(--t-500);
          font-weight: 600;
        }
        .nt-stat-value {
          font-size: 16px;
          font-weight: 700;
          color: var(--ink);
        }

        .network-rpc-detail {
          margin-top: 10px;
          font-size: 12px;
          color: var(--t-500);
        }

        .network-card-admin {
          display: flex;
          gap: 4px;
          justify-content: flex-end;
          margin-top: 14px;
          padding-top: 12px;
          border-top: 1px solid var(--line);
        }

        .btn-icon {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 7px;
          border: none;
          background: transparent;
          color: var(--t-700);
          cursor: pointer;
          border-radius: var(--r-sm);
        }
        .btn-icon:hover { background: var(--bg-inset); color: var(--ink); }
        .btn-danger-icon:hover { background: var(--err-soft); color: var(--err); }

        .nt-state {
          grid-column: 1 / -1;
          background: var(--bg-surface);
          border: 1px solid var(--line);
          border-radius: var(--r-lg);
        }
        .nt-state .spinning { color: var(--ink-muted); margin-bottom: 12px; }
        .nt-empty-btn { margin: 16px auto 0; }

        /* Premium upsell banner */
        .nt-upsell {
          display: flex;
          align-items: center;
          gap: 16px;
          padding: 18px 20px;
          margin-top: 16px;
        }
        .nt-upsell-icon {
          width: 44px;
          height: 44px;
          border-radius: var(--r-md);
          display: grid;
          place-items: center;
          background: var(--accent-soft);
          color: var(--accent);
          flex-shrink: 0;
        }
        .nt-upsell-copy { flex: 1; min-width: 0; }
        .nt-upsell-copy h3 {
          margin: 0 0 2px;
          font-size: 14px;
          font-weight: 700;
          color: var(--ink);
        }
        .nt-upsell-copy p {
          margin: 0;
          font-size: 13px;
          color: var(--ink-secondary);
        }
        .nt-upsell-btn { flex-shrink: 0; text-decoration: none; }

        /* Pagination footer */
        .pagination-bar {
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 12px;
          margin-top: 16px;
          font-size: 13px;
          color: var(--ink-muted);
        }
        .pagination-controls { display: flex; align-items: center; gap: 4px; }
        .page-btn {
          display: inline-flex; align-items: center; justify-content: center;
          width: 34px; height: 34px;
          border: 1px solid var(--line-strong);
          background: var(--bg-surface);
          color: var(--ink);
          border-radius: var(--r-sm);
          cursor: pointer;
        }
        .page-btn:hover:not(:disabled) { background: var(--bg-inset); }
        .page-btn:disabled { opacity: 0.4; cursor: default; }
        .page-indicator { padding: 0 10px; color: var(--ink); font-weight: 600; }

        @media (max-width: 576px) {
          .networks-grid { grid-template-columns: 1fr; }
          .page-btn { width: 44px; height: 44px; }
        }

        .spinning { animation: spin 1s linear infinite; }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        /* Shared button primitives used by modals (kept for modal markup) */
        .btn-primary {
          display: flex;
          align-items: center;
          gap: 8px;
          background: var(--primary);
          color: #fff;
          border: none;
          padding: 10px 16px;
          border-radius: var(--r-sm);
          cursor: pointer;
          font-weight: 600;
          font-size: 14px;
        }
        .btn-primary:hover { background: var(--primary-hover); }
        .btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }

        .btn-secondary {
          display: flex;
          align-items: center;
          gap: 8px;
          background: var(--bg-surface);
          color: var(--ink);
          border: 1px solid var(--line-strong);
          box-shadow: var(--sh-sm);
          padding: 10px 16px;
          border-radius: var(--r-sm);
          cursor: pointer;
          font-weight: 600;
          font-size: 14px;
        }
        .btn-secondary:hover { background: var(--bg-inset); }

        .btn-danger {
          background: var(--err);
          color: #fff;
          border: none;
          padding: 10px 16px;
          border-radius: var(--r-sm);
          cursor: pointer;
          font-weight: 600;
          font-size: 14px;
        }
        .btn-danger:hover { filter: brightness(0.93); }

        .btn-link {
          background: none;
          border: none;
          color: var(--accent);
          cursor: pointer;
          padding: 0;
          font-weight: 600;
        }

        /* Modal Styles */
        .modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(24, 24, 27, 0.45);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 16px;
        }
        .modal-container {
          background: var(--bg-surface);
          border: 1px solid var(--line);
          border-radius: var(--r-lg);
          width: 100%;
          max-width: 500px;
          max-height: 90vh;
          overflow-y: auto;
          box-shadow: var(--sh-pop);
        }
        .modal-large { max-width: 650px; }
        .modal-small { max-width: 400px; }

        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 18px 20px;
          border-bottom: 1px solid var(--line);
        }
        .modal-header h3 {
          margin: 0;
          font-size: 16px;
          font-weight: 700;
          letter-spacing: -0.01em;
          color: var(--ink);
        }
        .modal-close {
          background: none;
          border: none;
          color: var(--ink-secondary);
          cursor: pointer;
          padding: 6px;
          border-radius: var(--r-sm);
        }
        .modal-close:hover { background: var(--bg-inset); color: var(--ink); }

        .modal-body { padding: 20px; }
        .modal-body p { color: var(--ink-secondary); font-size: 14px; }

        .modal-footer {
          display: flex;
          justify-content: flex-end;
          gap: 10px;
          padding: 14px 20px;
          border-top: 1px solid var(--line);
          background: var(--bg-subtle);
          border-radius: 0 0 var(--r-lg) var(--r-lg);
        }

        .form-group { margin-bottom: 16px; }
        .form-group label {
          display: block;
          font-size: 13px;
          font-weight: 600;
          color: var(--ink);
          margin-bottom: 7px;
        }
        .form-group input,
        .form-group select {
          width: 100%;
          padding: 9px 12px;
          border: 1px solid var(--line-strong);
          border-radius: var(--r-sm);
          font-size: 14px;
          background: var(--bg-surface);
          color: var(--ink);
          transition: border-color 0.15s, box-shadow 0.15s;
        }
        .form-group input:focus,
        .form-group select:focus {
          outline: none;
          border-color: var(--accent);
          box-shadow: 0 0 0 3px var(--accent-soft);
        }
        .input-error { border-color: var(--err) !important; }
        .error-text {
          display: flex;
          align-items: center;
          gap: 4px;
          color: var(--err);
          font-size: 12px;
          margin-top: 5px;
        }
        .form-hint {
          display: block;
          font-size: 12px;
          color: var(--ink-muted);
          margin-top: 5px;
        }
        .form-section-divider {
          display: flex;
          align-items: center;
          margin: 22px 0 14px;
          color: var(--ink-muted);
          font-size: 12px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.04em;
        }
        .form-section-divider::before,
        .form-section-divider::after {
          content: '';
          flex: 1;
          border-bottom: 1px solid var(--line);
        }
        .form-section-divider span { padding: 0 12px; }

        .form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 14px;
        }
        @media (max-width: 600px) {
          .form-row { grid-template-columns: 1fr; }
        }

        .checkbox-label {
          display: flex !important;
          align-items: center;
          gap: 8px;
          cursor: pointer;
        }
        .checkbox-label input[type="checkbox"] {
          width: auto;
          cursor: pointer;
        }
      `}</style>
    </div>
  );
};

export default Networks;
